/**
 * LH-51 (LH-38 Phase 2) — Contract Partner Purchase Order.
 *
 * Named-key model (NOT the QuickBase-fid-indexed style used by e.g. FileMeta /
 * CPBillingCode). That is deliberate: the backend renders the PO server-side and
 * returns render-ready JSON, because the DEV and PROD "Generate CP PO" code pages
 * read line items from two different tables with two different rate-resolution
 * rules. Keeping that divergence inside Flask is the whole point of the design —
 * this file must therefore contain NO QuickBase field ids and NO business rules.
 *
 * The only thing done here is display formatting, mirroring the code page:
 *   money → Intl en-US USD, 2 dp        (ContractPartnerPO.js renderLineItems)
 *   qty   → Intl en-US number format
 * Formatted strings are computed once in the constructor rather than exposed as
 * getters, so template bindings stay O(1) per change-detection pass.
 */

const MONEY_FORMAT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

const QTY_FORMAT = new Intl.NumberFormat('en-US');

/** Currency string exactly as the manager-facing code page renders it. */
export const formatPoMoney = (value: any): string =>
  MONEY_FORMAT.format(Number(value) || 0);

/** Quantity string exactly as the manager-facing code page renders it. */
export const formatPoQty = (value: any): string =>
  QTY_FORMAT.format(Number(value) || 0);

/**
 * Brand logos ship as webview assets, selected by the `logoKey` the backend
 * resolves ('sfi' | 'lighthouse' | 'am-comm').
 *
 * They are NOT loaded from QuickBase. The manager-facing code page points <img>
 * at QuickBase /up/ file-attachment paths, which only resolve for a browser
 * carrying a QuickBase session cookie — an unauthenticated GET answers 404. A
 * Contract Partner has no such session (that is the whole premise of LH-51), and
 * a token must never ship to a partner's browser, so the bytes have to come from
 * here. Assets are relative to the app base, so this also works under the
 * `/daily-importer-cp/:guid` route.
 */
const PO_LOGO_ASSETS: { [key: string]: string } = {
  sfi: 'assets/logos/sfi.png',
  lighthouse: 'assets/logos/lighthouse.png',
  amComm: 'assets/logos/am-comm.png',
};

/** The "from" block — who the PO is issued by. Resolved by the backend. */
export class CpPoFrom {
  name?: string;
  address?: string;
  cityState?: string;
  phone?: string;
  /** Resolved bundled-asset path, or undefined for an unrecognised brand. */
  logoUrl?: string;

  constructor(data?: any) {
    if (!data) {
      return;
    }

    this.name = data.name;
    this.address = data.address;
    this.cityState = data.cityState;
    this.phone = data.phone;
    this.logoUrl = PO_LOGO_ASSETS[data.logoKey];
  }
}

export class CpPoHeader {
  poNumber?: string;
  date?: string;
  cpName?: string;
  cpEmail?: string;
  jobId?: string;
  jobName?: string;
  projectNumber?: string;
  brand?: string;
  from?: CpPoFrom;

  constructor(data?: any) {
    if (!data) {
      return;
    }

    this.poNumber = data.poNumber;
    this.date = data.date;
    this.cpName = data.cpName;
    this.cpEmail = data.cpEmail;
    this.jobId = data.jobId;
    this.jobName = data.jobName;
    this.projectNumber = data.projectNumber;
    this.brand = data.brand;
    this.from = new CpPoFrom(data.from);
  }
}

export class CpPoLineItem {
  planQty?: number;
  billingCode?: string;
  description?: string;
  rate?: number;
  amount?: number;
  /** Backend flag: the CP rate could not be resolved, so rate/amount are $0. */
  unresolved?: boolean;

  planQtyFormatted?: string;
  rateFormatted?: string;
  amountFormatted?: string;

  constructor(data?: any) {
    if (!data) {
      return;
    }

    this.planQty = data.planQty;
    this.billingCode = data.billingCode;
    this.description = data.description;
    this.rate = data.rate;
    this.amount = data.amount;
    this.unresolved = data.unresolved === true;

    this.planQtyFormatted = formatPoQty(this.planQty);
    this.rateFormatted = formatPoMoney(this.rate);
    this.amountFormatted = formatPoMoney(this.amount);
  }
}

export class CpPo {
  success?: boolean;
  /** false when the (optional, server-side) manager share gate is not satisfied. */
  shared?: boolean;
  header?: CpPoHeader;
  lineItems: CpPoLineItem[] = [];
  total?: number;
  totalFormatted?: string;

  constructor(data?: any) {
    if (!data) {
      return;
    }

    this.success = data.success;
    this.shared = data.shared;

    if (data.header) {
      this.header = new CpPoHeader(data.header);
    }

    this.lineItems = (data.lineItems || []).map((li: any) => new CpPoLineItem(li));

    this.total = data.total;
    this.totalFormatted = formatPoMoney(this.total);
  }
}
